import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Bot, Mail, ClipboardList, CheckCircle2, Clock, RefreshCw, Loader2, Send, Zap,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

interface AgentAction {
  id: string;
  type: string;
  origin?: string;
  to?: string;
  contact_hint?: string;
  subject?: string;
  title?: string;
  body?: string;
  notes?: string;
  priority?: string;
  status: string;
  reason?: string;
  created_at: string;
}

interface CSRun {
  id: string;
  stale_count: number;
  actions_created: number;
  summary?: string;
  created_at: string;
}

export default function AgentOrchestratorPanel() {
  const [actions, setActions] = useState<AgentAction[]>([]);
  const [runs, setRuns] = useState<CSRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string>("");
  const [intent, setIntent] = useState("");
  const [lastExec, setLastExec] = useState<{ user_message: string; tool_calls: unknown[] } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, r] = await Promise.all([
        fetch(`${BACKEND_URL}/api/orchestrator/actions/recent?limit=20`, { credentials: "include" }).then(x => x.json()),
        fetch(`${BACKEND_URL}/api/cs/runs/recent?limit=5`, { credentials: "include" }).then(x => x.json()),
      ]);
      setActions(a.items || []);
      setRuns(r.items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const triggerScan = async () => {
    setScanning(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/cs/scan`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (d.skipped) {
        toast.info("Agent CS désactivé dans la policy");
      } else {
        toast.success(`Scan : ${d.actions_created || 0} actions créées (${d.stale_count || 0} prospects silencieux)`);
      }
      await load();
    } catch { toast.error("Erreur du scan"); }
    setScanning(false);
  };

  const executeIntent = async () => {
    if (!intent.trim()) return;
    setExecuting(true);
    setLastExec(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/orchestrator/execute`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intent }),
      });
      const d = await r.json();
      setLastExec({ user_message: d.user_message, tool_calls: d.tool_calls });
      setIntent("");
      await load();
      toast.success("Grafton a exécuté la demande");
    } catch { toast.error("Erreur d'exécution"); }
    setExecuting(false);
  };

  const confirm = async (a: AgentAction) => {
    setConfirmingId(a.id);
    try {
      await fetch(`${BACKEND_URL}/api/orchestrator/actions/${a.id}/confirm`, { method: "POST", credentials: "include" });
      toast.success("Action confirmée");
      await load();
    } catch { toast.error("Erreur"); }
    setConfirmingId("");
  };

  return (
    <div className="space-y-4" data-testid="agent-orchestrator-panel">
      {/* Header + quick execute */}
      <Card className="p-5 border-primary/30">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-11 w-11 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0">
            <Bot className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold">Grafton Autonome</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Grafton exécute des actions réelles (emails, tâches CRM, relances) en mode préparation — tu valides avant l'envoi réel.
            </p>
          </div>
          <Button size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={triggerScan} disabled={scanning} data-testid="cs-trigger-scan">
            {scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
            Lancer scan CS
          </Button>
        </div>

        <div className="flex gap-2">
          <Input
            placeholder="Ex: Prépare une relance pour jean@acme.fr qui n'a pas répondu à l'audit"
            value={intent}
            onChange={(e) => setIntent(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") executeIntent(); }}
            data-testid="intent-input"
          />
          <Button onClick={executeIntent} disabled={executing || !intent.trim()} className="gap-1.5 shrink-0" data-testid="intent-send">
            {executing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Exécuter
          </Button>
        </div>

        {lastExec && (
          <div className="mt-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
            <p className="text-sm">{lastExec.user_message}</p>
            {Array.isArray(lastExec.tool_calls) && lastExec.tool_calls.length > 0 && (
              <p className="text-[10px] text-muted-foreground mt-1">
                Tools appelés : {lastExec.tool_calls.map((t, i) => {
                  const tc = t as { name?: string };
                  return <Badge key={i} variant="outline" className="text-[9px] mr-1">{tc.name || "?"}</Badge>;
                })}
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Recent CS runs */}
      {runs.length > 0 && (
        <Card className="p-5" data-testid="cs-runs">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-primary" />
            <h4 className="text-sm font-semibold uppercase tracking-wider">Scans CS récents</h4>
          </div>
          <div className="space-y-2">
            {runs.slice(0, 5).map(r => (
              <div key={r.id} className="flex items-center gap-3 p-2 rounded-lg border border-border/40">
                <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Bot className="h-3.5 w-3.5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[9px]">{r.actions_created || 0} actions</Badge>
                    <span className="text-[10px] text-muted-foreground">{r.stale_count} prospects silencieux</span>
                    <span className="text-[10px] text-muted-foreground">· {new Date(r.created_at).toLocaleString("fr-FR")}</span>
                  </div>
                  {r.summary && <p className="text-xs text-muted-foreground mt-0.5 truncate">{r.summary}</p>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Recent actions */}
      <Card className="p-5" data-testid="agent-actions">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-primary" />
            <h4 className="text-sm font-semibold uppercase tracking-wider">
              Actions préparées ({actions.length})
            </h4>
          </div>
          <Button size="sm" variant="ghost" className="gap-1.5 h-7" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </Button>
        </div>
        {actions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">Aucune action préparée. Utilise le champ ci-dessus ou lance un scan CS.</p>
        ) : (
          <div className="space-y-2">
            {actions.map(a => (
              <div key={a.id} className="p-3 rounded-lg border border-border/40 hover:border-primary/30 transition-colors" data-testid={`action-row-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${a.type === "email_draft" ? "bg-blue-500/15 text-blue-400" : a.type === "crm_task" ? "bg-amber-500/15 text-amber-400" : "bg-primary/15 text-primary"}`}>
                    {a.type === "email_draft" ? <Mail className="h-4 w-4" /> : <ClipboardList className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant="outline" className="text-[9px]">{a.type.replace("_", " ")}</Badge>
                      {a.origin === "cs_agent" && <Badge variant="outline" className="text-[9px] text-primary">CS Agent</Badge>}
                      {a.origin === "lead_hunter_intro" && <Badge variant="outline" className="text-[9px] text-emerald-400 border-emerald-500/30">Lead Hunter</Badge>}
                      {a.status === "confirmed" && <Badge variant="outline" className="text-[9px] text-emerald-400 border-emerald-500/30">Confirmé</Badge>}
                      {a.status === "prepared" && <Badge variant="outline" className="text-[9px] text-amber-400 border-amber-500/30">À valider</Badge>}
                      <span className="text-[10px] text-muted-foreground truncate">{a.to || a.contact_hint}</span>
                    </div>
                    <p className="text-sm font-medium truncate">{a.subject || a.title}</p>
                    {a.reason && <p className="text-[11px] text-muted-foreground italic mt-0.5">{a.reason}</p>}
                    {a.body && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.body}</p>}
                  </div>
                  {a.status === "prepared" && (
                    <Button size="sm" variant="outline" className="gap-1 shrink-0" onClick={() => confirm(a)} disabled={confirmingId === a.id} data-testid={`confirm-${a.id}`}>
                      {confirmingId === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                      Confirmer
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
